import React, { useState } from 'react';

/**
 * Data Health Section
 *
 * Real-time metrics dashboard showing current data health.
 * Includes coverage visualization and mock Drive view.
 */
const DataHealthSection = ({ content, healthData, loading }) => {
  if (loading) {
    return (
      <div className="df-loading">
        <div className="df-loading__spinner" />
        <p>Loading data health metrics...</p>
      </div>
    );
  }

  return (
    <div className="df-health">
      <header className="df-section__header">
        <h2 className="df-section__title">{content.title}</h2>
        <p className="df-section__subtitle">{content.subtitle}</p>
      </header>

      {/* Metrics Cards */}
      <div className="df-metrics-grid">
        <MetricCard
          icon="file-text"
          value={healthData?.transcripts?.total || 0}
          label="Total Transcripts"
          sublabel="In our system"
        />
        <MetricCard
          icon="link"
          value={healthData?.transcripts?.linked || 0}
          label="Linked to Students"
          sublabel={`${Math.round((healthData?.transcripts?.linked / healthData?.transcripts?.total) * 100) || 0}% of total`}
          variant="success"
        />
        <MetricCard
          icon="unlink"
          value={healthData?.transcripts?.unlinked || 0}
          label="Unlinked"
          sublabel="Need student assignment"
          variant="warning"
        />
        <MetricCard
          icon="cpu"
          value={healthData?.transcripts?.summarized || 0}
          label="AI Summarized"
          sublabel={`${Math.round((healthData?.transcripts?.summarized / healthData?.transcripts?.total) * 100) || 0}% analyzed`}
        />
      </div>

      {/* Student Coverage */}
      <CoverageChart
        students={healthData?.students}
        legend={content.coverageLegend}
      />

      {/* Mock Google Drive View */}
      <MockDriveView
        tutorCount={healthData?.tutors?.active || 0}
        estimatedUncollected="60-80%"
      />
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard = ({ icon, value, label, sublabel, variant }) => {
  return (
    <div className={`df-metric-card ${variant ? `df-metric-card--${variant}` : ''}`}>
      <div className="df-metric-card__icon">
        <MetricIcon name={icon} />
      </div>
      <div className="df-metric-card__value">{value}</div>
      <div className="df-metric-card__label">{label}</div>
      {sublabel && <div className="df-metric-card__sublabel">{sublabel}</div>}
    </div>
  );
};

/**
 * Coverage Chart - Grid visualization of student data coverage
 */
const CoverageChart = ({ students, legend }) => {
  const [hoveredStudent, setHoveredStudent] = useState(null);

  const getCoverageLevel = (count) => {
    if (count === 0) return 'none';
    if (count <= 2) return 'low';
    if (count <= 5) return 'medium';
    return 'high';
  };

  const coverage = students?.coverage || [];
  const withData = students?.with_data || 0;
  const total = students?.total || 0;

  return (
    <div className="df-coverage-section">
      <div className="df-coverage-header">
        <div>
          <h3 className="df-coverage-title">Student Data Coverage</h3>
          <p style={{ fontSize: '0.85rem', color: '#64748B', marginTop: '0.25rem' }}>
            {withData} of {total} students have session data ({Math.round((withData / total) * 100) || 0}%)
          </p>
        </div>
        <div className="df-coverage-legend">
          {legend.map((item) => (
            <div key={item.level} className="df-coverage-legend__item">
              <div
                className="df-coverage-legend__swatch"
                style={{ background: item.color }}
              />
              {item.label}
            </div>
          ))}
        </div>
      </div>

      <div className="df-coverage-grid">
        {coverage.map((student, index) => {
          const level = getCoverageLevel(student.transcript_count);
          return (
            <div
              key={student.uuid || index}
              className={`df-coverage-cell df-coverage-cell--${level}`}
              onMouseEnter={() => setHoveredStudent(student)}
              onMouseLeave={() => setHoveredStudent(null)}
            >
              {student.first_name?.charAt(0) || '?'}
              {hoveredStudent?.uuid === student.uuid && (
                <div className="df-coverage-cell__tooltip">
                  {student.first_name}: {student.transcript_count} sessions
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Mock Google Drive View - Conceptual visualization of uncollected data
 */
const MockDriveView = ({ tutorCount, estimatedUncollected }) => {
  const mockFolders = [
    { name: 'Meet Recordings', files: '24 items', badge: 'uncollected' },
    { name: 'Session Notes', files: '18 items', badge: 'uncollected' },
    { name: 'Auto-generated Transcripts', files: '31 items', badge: 'uncollected' },
    { name: 'Student Documents', files: '12 items', badge: null }
  ];

  return (
    <div className="df-mock-drive">
      <div className="df-mock-drive__header">
        <svg className="df-mock-drive__logo" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
          <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
          <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
          <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
          <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
          <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
          <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
        </svg>
        <span className="df-mock-drive__title">Tutor Google Drives</span>
        <span className="df-mock-drive__subtitle">
          ~{estimatedUncollected} of data uncollected from {tutorCount} tutors
        </span>
      </div>
      <div className="df-mock-drive__content">
        {mockFolders.map((folder, index) => (
          <div key={index} className="df-mock-drive__folder">
            <svg className="df-mock-drive__folder-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
            </svg>
            <span className="df-mock-drive__folder-name">{folder.name}</span>
            <span className="df-mock-drive__folder-meta">{folder.files}</span>
            {folder.badge && (
              <span className={`df-mock-drive__badge df-mock-drive__badge--${folder.badge}`}>
                Not Synced
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Metric Icons
 */
const MetricIcon = ({ name }) => {
  const icons = {
    'file-text': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    link: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    unlink: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
        <path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
        <line x1="8" y1="2" x2="8" y2="5" />
        <line x1="2" y1="8" x2="5" y2="8" />
        <line x1="16" y1="19" x2="16" y2="22" />
        <line x1="19" y1="16" x2="22" y2="16" />
      </svg>
    ),
    cpu: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
        <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="1" x2="9" y2="4" />
        <line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" />
        <line x1="15" y1="20" x2="15" y2="23" />
      </svg>
    )
  };

  return icons[name] || <span>?</span>;
};

export default DataHealthSection;
